/** The tour record's header (plan §1 "Header"): the tour name; the subtitle reads
 *  tenant · ID · trip type · Fixed date / Recurring · time zone · bookings, each with its
 *  value actions. On the right the Actions menu and Edit (`E`) — in edit mode, Discard
 *  and Done (`Esc`). The Template / Guide group / Deleted chips are the identity strip's
 *  (`IdentityStrip`).
 *
 *  No `console_tours_manage` → no Edit button at all (a disabled one would advertise
 *  power the operator lacks).
 *
 *  Below 1100 px of content (no dock column) a **Preview** button opens the phone preview
 *  in a drawer (`onPreview`), in both modes. */
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import { Box, Button, Tooltip } from '@mui/material';

import type { TourDetail } from '@/api/tours';
import { PageHeader } from '@/components/PageHeader';
import { IdentitySubtitle } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { Kbd } from '@/features/records/page/Kbd';
import { RecordActionsMenu, type RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { toursHref } from '@/utils/gridLinks';

import { bespokeActions, tourBookingsActions, tripTypeActions, type TourCtx } from '../tourLinks';

export interface TourRecordHeaderProps {
  detail: TourDetail;
  /** The active tab's label (document title). */
  section: string;
  canEdit: boolean;
  editing: boolean;
  /** Unsaved changes — Discard has something to discard. */
  dirty: boolean;
  onEdit: () => void;
  onDone: () => void;
  onDiscard: () => void;
  actionGroups?: RecordActionGroup[];
  /** Below 1100 px of content: open the phone preview's drawer. */
  onPreview?: () => void;
  /** The drawer is open (`aria-expanded`). */
  previewOpen?: boolean;
}

export function TourRecordHeader({
  detail: t,
  section,
  canEdit,
  editing,
  dirty,
  onEdit,
  onDone,
  onDiscard,
  actionGroups = [],
  onPreview,
  previewOpen = false,
}: TourRecordHeaderProps) {
  const links: TourCtx = { tenantId: t.tenant?.id };
  const total = t.user_stats?.total;
  const bookings = typeof total === 'number' ? `${total} ${total === 1 ? 'booking' : 'bookings'}` : 'Bookings';

  const subtitle = (
    <IdentitySubtitle
      tenant={t.tenant}
      grid={t.tenant ? { href: toursHref([], t.tenant.id), label: 'Tours of this operator' } : null}
      id={t.id}
      extra={[
        <ValueActions key="type" actions={tripTypeActions(t.trip_type_display, links)}>
          {t.trip_type_display}
        </ValueActions>,
        <ValueActions key="bespoke" actions={bespokeActions(t.is_bespoke, links)}>
          {t.is_bespoke ? 'Fixed date' : 'Recurring'}
        </ValueActions>,
        ...(t.start_timezone ? [<span key="tz">{t.start_timezone}</span>] : []),
        <ValueActions key="bookings" actions={tourBookingsActions(t.id, links)}>
          {bookings}
        </ValueActions>,
      ]}
    />
  );

  // Preview · Actions · Discard · Done is wider than a 390 px page: the buttons wrap among
  // themselves (right-aligned) rather than overflow it.
  const actions = (
    <Box
      data-tour-header-actions
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 1,
        flex: 'none',
        maxWidth: '100%',
        '& .MuiButton-root': { whiteSpace: 'nowrap' },
      }}
    >
      {onPreview && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PhoneAndroidOutlinedIcon />}
          onClick={onPreview}
          aria-haspopup="dialog"
          aria-expanded={previewOpen}
          data-preview-open
        >
          Preview
        </Button>
      )}
      <RecordActionsMenu groups={actionGroups} />
      {editing ? (
        <>
          <Tooltip title={dirty ? 'Discard every change — ⌘Z brings them back' : 'No changes to discard'}>
            <span>
              <Button size="small" color="error" onClick={onDiscard} disabled={!dirty} data-discard>
                Discard
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" size="small" onClick={onDone} data-close-editor>
            Done
            <Kbd>Esc</Kbd>
          </Button>
        </>
      ) : (
        canEdit && (
          <Button variant="contained" size="small" startIcon={<EditOutlinedIcon />} onClick={onEdit} data-edit>
            Edit
            <Kbd>E</Kbd>
          </Button>
        )
      )}
    </Box>
  );

  return (
    <PageHeader
      helpStory="tours-grid-editor"
      section={section}
      title={t.name}
      titleText={t.name}
      subtitle={subtitle}
      actions={actions}
    />
  );
}
